import Vue from "vue";
import VueRouter from "vue-router";

import { StorageService } from "@/services/StorageService";
import { TrackingCodeService } from "@/services/TrackingCodeService";

Vue.use(VueRouter);

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/_widgets/screening-questions",
      name: "widget:screening-questions",
      component: () => import(/* webpackChunkName: "widgets" */ "@/widgets/screening-questions/ScreeningQuestions.vue"),
      props: (route) => ({
        opportunityId: route.query.opportunityRef,
        candidateGgId: route.query.ggId
      })
    },
    {
      path: "/:locale(en|es)?",
      name: "home",
      beforeEnter(to) {
        if (process.env.VUE_APP_DISCOVERY_ENVIRONMENT_MODE === "development") {
          window.location.href = `/dev`;
        } else {
          window.location.href = `${process.env.VUE_APP_DISCOVERY_HOMEPAGES_URL}/`;
        }
      }
    },
    {
      path: "/:locale(en|es)?/app",
      name: "homeApp",
      redirect: (route) => ({ name: "home", params: route.params, query: route.query, hash: route.hash })
    },
    {
      path: "/:locale(en|es)?/dev",
      name: "dev",
      component: () => import(/* webpackChunkName: "devenv" */ "@/pages/devenv/DevEnv.vue"),
      beforeEnter(to, from, next) {
        if (process.env.VUE_APP_DISCOVERY_ENVIRONMENT_MODE !== "development") {
          window.location.href = "404";
        } else {
          next();
        }
      }
    },
    {
      path: "/:locale(en|es)?/postings",
      name: "jobs:backward-compatibility",
      redirect: (route) => ({ name: "jobs", params: { target: "jobs" } })
    },
    {
      path: "/:locale(en|es)?/talent",
      name: "talent",
      beforeEnter() {
        window.location.href = `${process.env.VUE_APP_DISCOVERY_HOMEPAGES_URL}/search/people`;
      }
    },
    {
      path: "/:locale(en|es)?/findtalent",
      beforeEnter() {
        window.location.href = `${process.env.VUE_APP_DISCOVERY_HOMEPAGES_URL}/search/people`;
      }
    },
    {
      path: "/:locale(en|es)?/jobalerts",
      name: "opportunities",
      beforeEnter() {
        window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_BIO_URL}/preferences`;
      }
    },
    {
      path: "/:locale(en|es)?/talent/(.*)",
      name: "talentToJobs",
      beforeEnter(to, from, next) {
        window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_SELF_URL}${to.fullPath.replace("/talent/", "/postings/")}`;
      }
    },
    {
      path: "/:locale(en|es)?/postings/post-type",
      name: "postType",
      beforeEnter(to, from, next) {
        window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_VADER_URL}/post/onboarding`;
      }
    },
    {
      path: "/:locale(en|es)?/search",
      name: "search",
      redirect: (route) => ({ name: "people-name", params: { target: "people-name" }, query: route.query })
    },
    {
      path: "/:locale(en|es)?/search/people-name",
      name: "people-name",
      beforeEnter(to) {
        window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_RAIDEN_URL}/search/people-name`;
      }
    },
    {
      path: "/:locale(en|es)?/search/people-skill",
      name: "people-skill",
      beforeEnter(to) {
        window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_RAIDEN_URL}/search/people-skill`;
      }
    },
    {
      path: "/:locale(en|es)?/search/jobs",
      name: "jobs",
      beforeEnter(to) {
        window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_RAIDEN_URL}/search/jobs`;
      }
    },
    {
      path: "/:locale(en|es)?/postings/post",
      name: "post",
      beforeEnter(to, from, next) {
        window.location.href = `/post/onboarding`;
      }
    },
    {
      path: "/:locale(en|es)?/postings/:opportunityId/automessages",
      name: "automessages",
      component: () => import(/* webpackChunkName: "automesages" */ "@/pages/automessages/Automessages.vue"),
      props: (route) => ({
        columnCodeOrId: route.query.columnCodeOrId,
        opportunityId: route.params.opportunityId.split("-").shift(),
        backUrl: route.query.backUrl
      })
    },
    {
      path: "/:locale(en|es)?/postings/list",
      name: "list",
      component: () => import(/* webpackChunkName: "list" */ "@/pages/list/List.vue"),
      redirect: () => ({ name: "list:posted" }),
      children: [
        {
          name: "list:posted",
          path: "posted",
          component: () => import(/* webpackChunkName: "list:posting" */ "@/components/opportunities-list/OpportunitiesList.vue"),
          props: () => ({
            list: "posted"
          })
        },
        {
          name: "list:applications",
          path: "applications",
          component: () => import(/* webpackChunkName: "list:applications" */ "@/components/opportunities-list/OpportunitiesList.vue"),
          props: () => ({
            list: "applications"
          })
        },
        {
          name: "list:drafts",
          path: "drafts",
          component: () => import(/* webpackChunkName: "list:drafts" */ "@/components/opportunities-list/OpportunitiesList.vue"),
          props: () => ({
            list: "drafts"
          })
        },
        {
          name: "list:open",
          path: "open",
          component: () => import(/* webpackChunkName: "list:open" */ "@/components/opportunities-list/OpportunitiesList.vue"),
          props: () => ({
            list: "open"
          })
        },
        {
          name: "list:closed",
          path: "closed",
          component: () => import(/* webpackChunkName: "list:closed" */ "@/components/opportunities-list/OpportunitiesList.vue"),
          props: () => ({
            list: "closed"
          })
        },
      ]
    },
    {
      path: "/:locale(en|es)?/postings/applications",
      name: "applications",
      component: () => import(/* webpackChunkName: "applications-list" */ "@/pages/applications/Applications.vue")
    },
    {
      path: "/:locale(en|es)?/post/:id",
      name: "details:jobs"
    },
    {
      name: "pipeline",
      path: "/:locale(en|es)?/postings/:id/pipeline",
      component: () => import(/* webpackChunkName: "pipeline" */ "@/pages/pipeline/Pipeline.vue"),
      props: (route) => ({
        candidateToShow: {
          id: route.query.candidate,
          tab: route.query.tab
        },
        scrollToColumn: route.hash ? route.hash.substr(1) : undefined,
        opportunityId: route.params.id,
        dialog: route.hash
      })
    },
    {
      path: "/:locale(en|es)?/postings/:id",
      name: "details",
      redirect: (to) => ({ name: "details:posting", params: to.params }),
      children: [
        {
          name: "details:posting",
          path: "",
          beforeEnter(to, from, next) {
            window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_VADER_URL}/post/${to.params.id}`;
          }
        },
        {
          name: "details:posting-legacy",
          path: "posting",
          beforeEnter(to) {
            window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_VADER_URL}/post/${to.params.id}`;
          }
        },
        {
          name: "details:questions",
          path: "questions",
          beforeEnter(to, from, next) {
            if (to.query && to.query.question) {
              window.location.href =
                `${process.env.VUE_APP_DISCOVERY_UI_VADER_URL}/post/${to.params.id}?column=questions&question=${to.query.question}`;
            } else {
              window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_VADER_URL}/post/${to.params.id}?column=questions`;
            }
          }
        },
        {
          name: "details:dashboard",
          path: "dashboard",
          beforeEnter(to, from, next) {
            window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_VADER_URL}/post/${to.params.id}`;
          }
        },
        {
          name: "details:tracking",
          path: "tracking",
          redirect: { name: "pipeline" }
        },
        {
          name: "details:candidates",
          path: "candidates",
          redirect: { name: "pipeline" }
        },
        {
          name: "details:candidates-ranking",
          path: "candidates-ranking",
          beforeEnter(to, from, next) {
            window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_ARYA_URL}/applications/${to.params.id}`;
          }
        },
        {
          name: "details:match-and-rank",
          path: "match-and-rank",
          // tslint:disable-next-line max-line-length
          beforeEnter(to, from, next) {
            window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_VADER_URL}/post/${to.params.id}?column=matchandrank`;
          }
        }
      ],
      props: (route) => ({
        opportunityId: route.params.id,
        dialog: route.hash
      })
    },
    {
      path: "/:locale(en|es)?/postings/:opportunityId/candidates/:candidateId",
      name: "candidate",
      beforeEnter(to, from, next) {
        // tslint:disable-next-line max-line-length
        window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_ARYA_URL}/applications/${to.params.opportunityId}/candidates/${to.params.candidateId}`;
      },
    },
    {
      path: "/:locale(en|es)?/posting/:opportunityId/candidates/:candidateId",
      name: "candidate-legacy",
      beforeEnter(to, from, next) {
        // tslint:disable-next-line max-line-length
        window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_ARYA_URL}/applications/${to.params.opportunityId}/candidates/${to.params.candidateId}`;
      },
    },
    {
      path: "/:locale(en|es)?/messenger/:resource(conversations|rooms)?/:id?",
      name: "messenger",
      props: (route) => ({
        resource: route.params.resource,
        id: route.params.id
      })
    },
    {
      path: "/:locale(en|es)?/opportunities/onboarding/:step?",
      redirect: { name: "onboarding" }
    },
    {
      path: "/:locale(en|es)?/jobalerts/onboarding/:step?",
      name: "onboarding",
      beforeEnter(route) {
        const benefitsURL = route.params.locale ?
          `${route.params.locale}/benefits?flow=benefits-preferences` : `benefits?flow=benefits-preferences`;

        window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_BIO_URL}/${benefitsURL}`;
      }
    },
    {
      path: "/:locale(en|es)?/opportunities/benefits",
      redirect: { name: "benefits:opportunities" }
    },
    {
      path: "/:locale(en|es)?/jobalerts/benefits",
      name: "benefits:opportunities",
      beforeEnter(to) {
        window.location.href =
          `${process.env.VUE_APP_DISCOVERY_UI_BIO_URL}/benefits?flow=benefits-preferences&origin=preferences${to.hash}`;
      }
    },
    {
      path: "/:locale(en|es)?/opportunities/preferences",
      redirect: { name: "preferences" }
    },
    {
      path: "/:locale(en|es)?/jobalerts/preferences",
      name: "preferences",
      beforeEnter(to) {
        window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_BIO_URL}/preferences${to.hash}`;
      }
    },
    {
      path: "/:locale(en|es)?/jobalerts/preferences/notifications",
      name: "preferences:notifications",
      beforeEnter() {
        window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_BIO_URL}/preferences/notifications`;
      }
    },
    {
      path: "/:locale(en|es)?/opportunities/:interest/unsubscribe",
      redirect: (to) => {
        return { name: "preferences", query: to.query, hash: "#email-notifications" };
      }
    },
    {
      path: "/:locale(en|es)?/jobalerts/:interest/unsubscribe",
      redirect: (to) => {
        return { name: "preferences", query: to.query, hash: "#email-notifications" };
      }
    },
    {
      path: "/:locale(en|es)?/postings/:opportunityId/edit-deadline",
      name: "deadline",
      beforeEnter(to) {
        // tslint:disable-next-line max-line-length
        window.location.href = `${process.env.VUE_APP_DISCOVERY_UI_VADER_URL}/post/${to.params.opportunityId}?dialog=deadline`;
      }
    },
    {
      path: "/:locale(en|es)?/postings/:opportunityId/set-alert",
      name: "setAlert",
      component: () => import(/* webpackChunkName: "set-alert" */ "@/pages/set-alert/SetAlert.vue"),
      props: (route) => ({
        opportunityId: route.params.opportunityId,
        step: route.query.step ? route.query.step : "signals"
      })
    },
    {
      path: "/:locale(en|es)?/postings/:opportunityId/member-preferences",
      name: "memberPreferences",
      component: () => import(/* webpackChunkName: "member-preferences" */ "@/pages/member-preferences/MemberPreferences.vue"),
      props: (route) => ({
        opportunityId: route.params.opportunityId,
        next: route.query.next,
        sharingToken: route.query.sharingToken
      })
    },
    {
      path: "/:locale(en|es)?/postings/:opportunityId/members-list",
      name: "membersList",
      component: () => import(/* webpackChunkName: "members-list" */ "@/pages/team-members-list/TeamMembersList.vue"),
      props: (route) => ({
        opportunityId: route.params.opportunityId,
        sharingToken: route.query.sharingToken
      })
    },
    {
      path: "/:locale(en|es)?/postings/:opportunityId/members-list/confirmed",
      name: "membersListConfirmed",
      component: () => import(/* webpackChunkName: "members-list-confirmed" */ "@/pages/team-member-confirmed/TeamMemberConfirmed.vue"),
      props: (route) => ({
        opportunityId: route.params.opportunityId
      })
    },
    {
      path: "/:locale(en|es)?/postings/:opportunityId/invitation-unavailable",
      name: "invitationUnavailable",
      component: () => import(/* webpackChunkName: "invitation-unavailable" */ "@/pages/invitation-unavailable/InvitationUnavailable.vue"),
      props: (route) => ({
        opportunityId: route.params.opportunityId,
        sharingToken: route.query.sharingToken
      })
    },
    {
      path: "/:locale(en|es)?/notifications-phrases/toggle",
      name: "notifications-phrases:toggle",
      // tslint:disable-next-line max-line-length
      component: () => import(/* webpackChunkName: "notifications-phrases-toggle" */ "@/pages/notifications-phrases/NotificationsPhrasesToggle.vue")
    },
    {
      path: "/:locale(en|es)?/404",
      name: "404",
      component: () => import("@/pages/error/Error.vue"),
      props: () => ({
        statusCode: 404
      })
    },
    {
      path: "/:locale(en|es)?/500",
      name: "500",
      component: () => import("@/pages/error/Error.vue"),
      props: () => ({
        statusCode: 500
      })
    },
    {
      path: "/:locale(en|es)?/*",
      name: "error",
      beforeEnter(to) {
        window.location.href = `/404`;
      }
    }
  ],
  scrollBehavior(to, from, savedPosition: any) {
    if (savedPosition || to.hash) {
      return savedPosition;
    } else {
      return {
        x: 0,
        y: 0
      };
    }
  }
});

router.beforeEach((to, from, next) => {
  let { utm_campaign, utm_source, utm_medium } = to.query;

  if (utm_campaign && utm_source && utm_medium) {
    if (typeof window !== "undefined") {
      if (utm_campaign === "google_jobs_apply") {
        utm_campaign = "organic";
        utm_source = "web";
        utm_medium = "google_jobs";
      } else if (utm_campaign === "jobs_on_facebook") {
        utm_campaign = "organic";
        utm_source = "web";
        utm_medium = "fb_jobs";
      }
      if (utm_campaign.startsWith("sid_")) {
        utm_campaign = window.atob(utm_campaign.substring(4));
      }
    }
    TrackingCodeService.addTrackingCode({
      utmCampaign: utm_campaign,
      utmSource: utm_source,
      utmMedium: utm_medium,
      path: to.path
    }).subscribe((codeId) => {
      StorageService.storeTrackingCode(codeId);
    }, (error) => {
      // tslint:disable-next-line:no-console
      console.log("Error saving tracking code", error);
    });
  }

  const backBtnAtScreeningQuestions = from.name === "candidate"
    && from.query.hasOwnProperty("step")
    && from.params.hasOwnProperty("opportunityId")
    && from.query.step === "screening-questions"
    && Object.keys(to.query).length === 0;
  if (backBtnAtScreeningQuestions) {
    window.location.href = `/post/${from.params.opportunityId}`;
    next(false);
  }

  const { communityToken } = to.query;

  if (communityToken && communityToken.length > 0 && communityToken.trim().length > 0) {
    StorageService.storeCommunityToken(communityToken);
  }

  next();
});

export default router;
